import axios from "axios";
import { resolveApiUrl } from "../../assets/GlobalVariables";
import { getSessionId } from "../../services/authService";

/** מקסימום הזמנות אחרונות להצגה במגירת פנייה לאופרציה. */
export const RECENT_OPERATION_REQUEST_LIMIT = 3;

const RECENT_ORDERS_PATH = "/api/agents/me/recent-orders";

function recentOrdersUrl() {
  return resolveApiUrl(RECENT_ORDERS_PATH);
}

/**
 * ממפה שורת API → אובייקט בשכבת הבחירה (תואם לכרטיסי pick / טופס פנייה).
 * @param {unknown} row
 * @returns {object|null}
 */
export function mapRecentOrderApiRowToPickerOrder(row) {
  if (!row || typeof row !== "object") return null;

  const id = row.id != null ? String(row.id).trim() : "";
  const orderId = row.orderId != null ? String(row.orderId).trim() : id;
  if (!id && !orderId) return null;

  const displayOrderNumber =
    row.displayOrderNumber != null && String(row.displayOrderNumber).trim() !== ""
      ? String(row.displayOrderNumber).trim()
      : null;

  const rawType =
    row.rawOrderType != null && String(row.rawOrderType).trim() !== ""
      ? String(row.rawOrderType).trim()
      : "unknown";
  const category =
    row.category != null && String(row.category).trim() !== ""
      ? String(row.category).trim()
      : rawType;

  const keyId = id || orderId;

  return {
    key: `recent-${rawType}-${keyId}`,
    id: keyId,
    orderId: orderId || keyId,
    displayOrderNumber,
    bookingRef: displayOrderNumber,
    mainContact:
      row.customerName != null && String(row.customerName).trim() !== ""
        ? String(row.customerName).trim()
        : "",
    hotelName:
      row.hotelOrDestination != null && String(row.hotelOrDestination).trim() !== ""
        ? String(row.hotelOrDestination).trim()
        : "",
    checkIn:
      row.startDate != null && String(row.startDate).trim() !== ""
        ? String(row.startDate).trim()
        : "",
    checkOut:
      row.endDate != null && String(row.endDate).trim() !== ""
        ? String(row.endDate).trim()
        : "",
    status:
      row.status != null && String(row.status).trim() !== ""
        ? String(row.status).trim()
        : "",
    category,
    rawOrderType: rawType,
  };
}

/**
 * טוען הזמנות אחרונות של הסוכן המחובר בלבד (POST /api/agents/me/recent-orders).
 * לא משתמש ב־getRecentOrdersAll ובלא bundle של 6 חודשים.
 *
 * @param {{ limit?: number }} [options]
 * @returns {Promise<{ orders: object[], meta: { source: string, limit: number, count: number, error?: string, message?: string } }>}
 */
export async function fetchRecentOrdersForOperationRequest(options = {}) {
  const requested =
    typeof options.limit === "number" && Number.isFinite(options.limit)
      ? Math.floor(options.limit)
      : RECENT_OPERATION_REQUEST_LIMIT;
  const limit = Math.min(Math.max(1, requested), RECENT_OPERATION_REQUEST_LIMIT);

  const fail = (message, error = "fetch_failed") => ({
    orders: [],
    meta: {
      source: "agent_recent_orders_endpoint",
      limit,
      count: 0,
      error,
      message,
    },
  });

  try {
    const sessionID = await getSessionId();
    if (!sessionID) {
      const message = "agent_recent_orders_no_session";
      console.warn("[OP_REQUEST_RECENT_ORDERS_LOAD_FAILED]", { message, error: "no_session" });
      return fail(message, "no_session");
    }

    const sid = String(sessionID).trim();
    const url = recentOrdersUrl();
    const res = await axios.post(
      url,
      { limit, sessionID: sid },
      {
        headers: {
          "Content-Type": "application/json",
          "X-Session-ID": sid,
        },
        withCredentials: true,
        timeout: 20000,
      }
    );

    const data = res?.data && typeof res.data === "object" ? res.data : {};
    const items = Array.isArray(data.items) ? data.items : [];
    const orders = items.map(mapRecentOrderApiRowToPickerOrder).filter(Boolean);
    const count = orders.length;
    const responseLimit =
      typeof data.limit === "number" && Number.isFinite(data.limit)
        ? Math.floor(data.limit)
        : limit;

    console.log("[OP_REQUEST_RECENT_ORDERS_ENDPOINT_CONNECTED]", {
      limit: responseLimit,
      count,
    });

    return {
      orders,
      meta: {
        source: "agent_recent_orders_endpoint",
        limit: responseLimit,
        count,
      },
    };
  } catch (e) {
    const message =
      e?.response?.data?.message != null
        ? String(e.response.data.message)
        : e?.message != null
          ? String(e.message)
          : "recent_orders_request_failed";
    const error =
      e?.response?.data?.error != null ? String(e.response.data.error) : "fetch_failed";

    console.warn("[OP_REQUEST_RECENT_ORDERS_LOAD_FAILED]", {
      message,
      error,
      status: e?.response?.status ?? null,
    });

    return fail(message, error);
  }
}
